import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, queryString } from "@/lib/api";
import type { Customer, Page } from "@/lib/types";

/**
 * Search-and-pick an existing customer record. Emits the customer id (or null
 * when cleared) plus its display label. Manual bookings can skip this entirely
 * and just type a free-text name.
 */
export function CustomerPicker({
  value,
  label,
  onChange,
}: {
  value: string | null;
  label?: string | null;
  onChange: (id: string | null, label: string | null) => void;
}) {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const [resolved, setResolved] = useState<string | null>(label ?? null);

  // When we were handed an id but no label (edit view), fetch the name once.
  const detail = useQuery({
    queryKey: ["customer", value],
    queryFn: () => api.get<Customer>(`/customers/${value}`),
    enabled: !!value && !resolved,
  });
  useEffect(() => {
    if (detail.data) setResolved(detail.data.full_name);
  }, [detail.data]);

  const results = useQuery({
    queryKey: ["customer-search", term],
    queryFn: () =>
      api.get<Page<Customer>>(
        `/customers${queryString({ q: term, limit: 8 })}`,
      ),
    enabled: term.trim().length >= 2 && !value,
  });

  if (value) {
    return (
      <div className="nm-field-box flex items-center gap-2 rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm">
        <span className="truncate text-sand">
          {resolved ?? detail.data?.full_name ?? "…"}
        </span>
        <button
          type="button"
          className="ml-auto text-xs text-sand-muted hover:text-gold"
          onClick={() => {
            setResolved(null);
            setTerm("");
            onChange(null, null);
          }}
        >
          {t("common.clear")}
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className="w-full rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none"
        placeholder={t("customers.searchPlaceholder")}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />
      {results.data && results.data.items.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-olive-600 bg-olive-800 text-sm shadow-lg">
          {results.data.items.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left hover:bg-olive-700"
                onClick={() => {
                  setResolved(c.full_name);
                  onChange(c.id, c.full_name);
                }}
              >
                {c.full_name}
                {c.company ? (
                  <span className="text-sand-muted"> · {c.company}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
